/* ============================================================
   ordersFocusView — d17ac4ce: pure resolution model for the
   /orders/:id email deep-link focus (spec:
   docs/orders-deeplink-acceptance-a2da1174.md, unblocks 99ba6cff).
   Loaded rows + focus id in, one honest resolution out:

     in-page  → the target is among the loaded rows (C1): open it
                straight from the list row, zero extra requests;
     direct   → not in the loaded page (C2 owned beyond page one,
                C3 foreign Guid, C4 unknown id): the caller may open
                OrderDetailView by id — the API 404s foreign/unknown,
                so no data can leak;
     miss     → no usable focus value at all (null/blank): plain list.

   Pure and dependency-free so it runs under `node --test`
   (tests/ordersFocusView.test.ts) as well as in the app bundle,
   mirroring the ordersPagerView pattern.
   ============================================================ */

export type OrdersFocusResolution =
  | { mode: 'in-page'; rowId: string }
  | { mode: 'direct'; id: string }
  | { mode: 'miss' };

/** s7: order-id comparison ignores case and dashes — the email link,
    the URL segment and the API serialization all carry the same Guid,
    but a hand-typed or uppercased variant must still match. Braces are
    deliberately *not* stripped: a braced literal is not something any
    real surface emits, and tolerating it would widen matching beyond
    the Guid route's own parsing. */
export function normalizeOrderId(id: string): string {
  return id.trim().toLowerCase().replace(/-/g, '');
}

const MISS: OrdersFocusResolution = { mode: 'miss' };

export function resolveOrdersFocus(
  rows: ReadonlyArray<{ orderId: string }>,
  focusOrderId: string | null | undefined,
): OrdersFocusResolution {
  if (focusOrderId == null) return MISS;
  const trimmed = focusOrderId.trim();
  const want = normalizeOrderId(focusOrderId);
  if (!want) return MISS;
  const hit = rows.find((o) => normalizeOrderId(o.orderId) === want);
  if (hit) return { mode: 'in-page', rowId: hit.orderId };
  // Direct fetch keeps the caller-supplied spelling (trimmed only, dashes
  // intact) — normalizeOrderId strips dashes, which the guid route needs.
  return { mode: 'direct', id: trimmed };
}
